import React, { useEffect, useState } from "react";
import { Container, Header, Icon, List, Loader, Message, Segment } from "semantic-ui-react";
import { DBStats as DBStatsData, GetDBStats } from "../Utils/UserAPI";

export default function DBStats() {
    const [stats, setStats] = useState<DBStatsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadData = () => {
        setLoading(true);
        setError(null);
        setForbidden(false);

        GetDBStats((statusCode, data) => {
            if (statusCode === 200 && data) {
                setStats(data);
            } else if (statusCode === 403) {
                setForbidden(true);
            } else {
                setError("Failed to load database stats");
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        loadData();
    }, []);

    if (loading) {
        return (
            <Container>
                <Loader active size="large">
                    Loading database stats...
                </Loader>
            </Container>
        );
    }

    if (forbidden) {
        return (
            <Container>
                <Message negative>
                    <Message.Header>Not authorized</Message.Header>
                    <p>You need user-admin permissions to view database stats.</p>
                </Message>
            </Container>
        );
    }

    if (error || !stats) {
        return (
            <Container>
                <Message negative>
                    <Message.Header>Error</Message.Header>
                    <p>{error || "Failed to load database stats"}</p>
                </Message>
            </Container>
        );
    }

    return (
        <Container>
            <Header as="h2">
                <Icon name="chart bar" />
                <Header.Content>
                    Database Stats
                    <Header.Subheader>
                        An at-a-glance view of member database completeness
                    </Header.Subheader>
                </Header.Content>
            </Header>

            <Segment>
                <List bulleted relaxed>
                    <List.Item>
                        There are {stats.total_users} total users, of whom {stats.total_logged_in} have logged in.
                    </List.Item>
                    <List.Item>There are {stats.total_lp_users} LP users.</List.Item>
                    <List.Item>
                        There are {stats.deceased_count} deceased users and {stats.exiled_count} exiled users.
                    </List.Item>
                    <List.Item>
                        Privacy preferences:
                        <List.List>
                            <List.Item>LP Alumni Relations only: {stats.privacy_no_lp_count}</List.Item>
                            <List.Item>LP brothers only: {stats.privacy_lp_only_count}</List.Item>
                            <List.Item>ADP International: {stats.privacy_intl_count}</List.Item>
                        </List.List>
                    </List.Item>
                    <List.Item>
                        There are {stats.missing_email_only_count} LP users missing only email addresses.
                    </List.Item>
                    <List.Item>
                        There are {stats.missing_snail_mail_only_count} LP users missing only snail mail addresses.
                    </List.Item>
                    <List.Item>
                        There are {stats.missing_email_and_snail_mail_count} LP users missing both email and snail
                        mail addresses.
                    </List.Item>
                    <List.Item>
                        There are {stats.missing_pledge_class_count} LP users missing a pledge class.
                    </List.Item>
                    <List.Item>
                        There are {stats.missing_big_brother_count} LP users missing a big brother. (does not
                        include the original ten)
                    </List.Item>
                    <List.Item>There are {stats.missing_family_count} LP users missing a family.</List.Item>
                    <List.Item>
                        There are {stats.getting_monthly_email_count} LP users getting the monthly emails.
                    </List.Item>
                    <List.Item>
                        There are {stats.corporation_member_count} users who are corporation members, and{" "}
                        {stats.non_corporation_member_count} who are not.
                    </List.Item>
                    <List.Item>
                        There are {stats.ungeocoded_count} users who couldn&apos;t be geocoded.
                    </List.Item>
                </List>
            </Segment>
        </Container>
    );
}
